"use client";

import { User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { cn } from "~/lib/utils";

interface CameraOffPlaceholderProps {
  userName?: string | null;
  avatarUrl?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function CameraOffPlaceholder({
  userName,
  avatarUrl,
  size = "md",
  className,
}: CameraOffPlaceholderProps) {
  const getInitials = (name: string | null | undefined): string => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0]!.charAt(0).toUpperCase();
    }
    return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
  };

  const initials = getInitials(userName);
  const hasIdentity = !!userName || !!avatarUrl;

  const sizeClasses = {
    sm: "h-12 w-12",
    md: "h-24 w-24",
    lg: "h-32 w-32",
  };

  const iconSizes = {
    sm: "h-6 w-6",
    md: "h-12 w-12",
    lg: "h-16 w-16",
  };

  const textSizes = {
    sm: "text-lg",
    md: "text-3xl",
    lg: "text-4xl",
  };

  return (
    <div
      className={cn(
        "flex h-full w-full items-center justify-center bg-gray-800",
        className,
      )}
    >
      {hasIdentity ? (
        <Avatar className={cn(sizeClasses[size], "border-2 border-gray-700")}>
          {avatarUrl && <AvatarImage src={avatarUrl} alt={userName ?? "User"} />}
          <AvatarFallback
            className={cn(
              "bg-gray-700 text-white",
              textSizes[size],
            )}
          >
            {initials || <User className={iconSizes[size]} />}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div
          className={cn(
            "flex items-center justify-center rounded-full border-2 border-gray-700 bg-gray-700",
            sizeClasses[size],
          )}
        >
          <User className={cn("text-white/70", iconSizes[size])} />
        </div>
      )}
    </div>
  );
}

